import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Center,
  HStack,
  VStack,
  Text,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { DataTable } from '@gamma/ui/data-table';
import { Column as TableColumn, CellProps } from 'react-table';
import { i18n } from '@investigator/shared/localization';
import KeyboardArrowDownSharpIcon from '@gamma/ui/icons/KeyboardArrowDownSharpIcon';
import { CheckIcon } from '@chakra-ui/icons';

import { EntraSigninWithUser } from '@investigator/shared/generated/graphql';
import { usePerformEdrAction, EntityAction } from '../../../queries';
import { useManualPagination } from '@investigator/shared/hooks';
import { GraphQLReqStatus, LoadingSpinner } from '@gamma/ui/progress';
import moment from 'moment';
import {
  useEntitiesContext,
  useEntityEdrContext,
} from '@investigator/features/entities/context';
import { useIdentityIntegrationSettings } from '@investigator/features/integrations/hooks';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { AuthContext } from '@investigator/shared/providers';
import {
  UniversalLogoutIcon,
  ResetPasswordIcon,
  DisableAccountIcon,
  TagHighRiskIcon,
} from '@gamma/logos';

const { integrations, detections } = i18n.pages;
const { edrAction } = detections.entityEDR;

const {
  resetPasswordText,
  universallogoutText,
  tagHighRiskText,
  disableAccountText,
  enableAccountText,
  resetPasswordpopuplast,
  resetPasswordpopupfirst,
  universallogoutpopupfirst,
  universallogoutpopuplast,
  tagHighRiskpopup,
  disableAccountpopup,
  enableAccountpopup,
  passwordResetSuccess,
  logoutSuccess,
  highrisksuccess,
  disableSuccess,
  enableSuccess,
  displayNameAndEmail,
  jobTitleAndDepartment,
  riskDetail,
  userPrincipalName,
  appUsed,
  location,
  timestamp,
  actions,
  noDataFound,
  save,
  cancel,
} = integrations.entra;

interface EntraIDUserHistoryDataSectionProps {
  tenant: string;
}

export const EntraIDUserHistoryDataSection = ({
  tenant,
}: EntraIDUserHistoryDataSectionProps): JSX.Element => {
  const { identityIntegrationEnabled } = useIdentityIntegrationSettings();
  const { userPrivileges, userRole } = useContext(AuthContext);

  const { selectedEntity } = useEntitiesContext();
  const { tenantInfo } = selectedEntity || {};
  const { tenant_id = '' } = tenantInfo || {};

  const tenantPrivileges =
    tenant_id && userPrivileges ? userPrivileges[tenant_id] : undefined;
  const actionsEnabled = tenantPrivileges?.microsoft_entra_id_action_enabled;

  const { entraSigninHistory } = useEntityEdrContext();
  const {
    data: signinHistoryItems,
    totalItems,
    loading: signinHistoryLoading,
    error: signinHistoryError,
    fetchHistory,
  } = entraSigninHistory;

  const { detection_id } = selectedEntity || {};

  // Pagination hook
  const { offset, pageSize, pageIndex, pageCount, onFetchData, getPageCount } =
    useManualPagination<EntraSigninWithUser>({
      initialFetch: true,
    });

  // Sync pagination with context
  React.useEffect(() => {
    fetchHistory(offset, pageSize);
  }, [offset, pageSize, fetchHistory]);

  // Sync page count
  React.useEffect(() => {
    if (!signinHistoryLoading || totalItems > 0) {
      getPageCount({ total_items: totalItems, size: pageSize });
    }
  }, [totalItems, pageSize, getPageCount, signinHistoryLoading]);

  const historyItems = useMemo(
    () => signinHistoryItems || [],
    [signinHistoryItems],
  );

  // Modals state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    action: string;
    user: EntraSigninWithUser | null;
  }>({ isOpen: false, action: '', user: null });

  const [successAlert, setSuccessAlert] = useState<string>('');
  const [errorAlert, setErrorAlert] = useState<string>('');
  React.useEffect(() => {
    if (!successAlert) return;
    const t = window.setTimeout(() => setSuccessAlert(''), 5000);
    return () => window.clearTimeout(t);
  }, [successAlert]);

  // Mutation
  const [performAction, { loading: actionLoading }] = usePerformEdrAction({
    onCompleted: (data) => {
      if (data.performEdrAction) {
        setSuccessAlert(getSuccessMessage(modalState.action, modalState.user));
        handleCloseModal();
      } else {
        setErrorAlert('Failed to perform action. Please try again.');
      }
    },
    onError: (error) => {
      setErrorAlert('Failed to perform action. Please try again.');
      console.error(error);
    },
  });

  const transformEntraId = (tenantId: string, rawUserId: string): string => {
    if (!rawUserId) return '';
    // UUID format: 760461a7-0390-486f-a48d-0d954b58972e
    // Target format: {tenant}-microsoft_entra_id-identity_user-760461a7|0390|486f|a48d|0d954b58972e
    const sanitizedId = rawUserId.replace(/-/g, '|');
    return `${tenantId}-microsoft_entra_id-identity_user-${sanitizedId}`;
  };

  const openModal = (action: string, user: EntraSigninWithUser) => {
    setModalState({ isOpen: true, action, user });
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, action: '', user: null });
    setErrorAlert('');
  };

  const getDisplayName = (user: EntraSigninWithUser | null): string => {
    return (
      user?.user?.microsoft_entra_id?.display_name ||
      user?.signin?.microsoft_entra_id?.user_principal_name ||
      'User'
    );
  };

  const getModalTitle = (
    action: string,
    user: EntraSigninWithUser | null,
  ): string => {
    if (!user) return '';
    const name = getDisplayName(user);
    switch (action) {
      case 'reset':
        return `${resetPasswordText} for ${name}?`;
      case 'logout':
        return `${universallogoutText} for ${name}?`;
      case 'risk':
        return `${tagHighRiskText} for ${name}?`;
      case 'disable':
        return `${disableAccountText} for ${name}?`;
      case 'enable':
        return `${enableAccountText} for ${name}?`;
      default:
        return '';
    }
  };

  const getModalMessage = (
    action: string,
    user: EntraSigninWithUser | null,
  ): string => {
    if (!user) return '';
    const name = getDisplayName(user);
    switch (action) {
      case 'reset':
        return `${resetPasswordpopupfirst} ${name} ${resetPasswordpopuplast}`;
      case 'logout':
        return `${universallogoutpopupfirst} ${name} ${universallogoutpopuplast}`;
      case 'risk':
        return `${tagHighRiskpopup}`;
      case 'disable':
        return `${disableAccountpopup}`;
      case 'enable':
        return `${enableAccountpopup}`;
      default:
        return '';
    }
  };

  const getConfirmLabel = (action: string): string => {
    switch (action) {
      case 'reset':
        return resetPasswordText;
      case 'logout':
        return universallogoutText;
      case 'risk':
        return save;
      case 'disable':
        return disableAccountText;
      case 'enable':
        return enableAccountText;
      default:
        return '';
    }
  };

  const getSuccessMessage = (
    action: string,
    user: EntraSigninWithUser | null,
  ): string => {
    if (!user) return '';
    const name = getDisplayName(user);
    switch (action) {
      case 'reset':
        return `${passwordResetSuccess}`;
      case 'logout':
        return `${logoutSuccess} ${name}`;
      case 'risk':
        return `${name} ${highrisksuccess}`;
      case 'disable':
        return `${name} ${disableSuccess}`;
      case 'enable':
        return `${name} ${enableSuccess}`;
      default:
        return '';
    }
  };

  const handleConfirm = () => {
    if (!modalState.user || !tenant) return;

    const rawUserId =
      modalState.user.user?.microsoft_entra_id?.id ||
      modalState.user.signin?.microsoft_entra_id?.user_id;

    if (!rawUserId) return;

    const actionMap: Record<string, EntityAction> = {
      reset: 'password_reset',
      logout: 'universal_logout',
      risk: 'increase_risk_score',
      disable: 'disable_account',
      enable: 'enable_account',
    };

    const action = actionMap[modalState.action];
    if (!action) return;

    performAction({
      variables: {
        tenant,
        action,
        edr_device_input: {
          entity_id: transformEntraId(tenant, rawUserId),
          detection_id: detection_id,
        },
      },
    });
  };

  const dataTableColumns: TableColumn<EntraSigninWithUser>[] = useMemo(
    () => [
      {
        Header: displayNameAndEmail,
        accessor: (row) => row.user?.microsoft_entra_id?.display_name,
        id: 'displayName',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => {
          const userPayload = props.row.original.user?.microsoft_entra_id;
          const displayName =
            userPayload?.display_name ||
            props.row.original.signin?.microsoft_entra_id
              ?.user_principal_name ||
            '-';
          const email =
            userPayload?.mail || userPayload?.user_principal_name || '-';
          return (
            <VStack alignItems="start" spacing={0} w="100%">
              <Text noOfLines={1} fontSize="md" color="text.primary">
                {displayName}
              </Text>
              <Text noOfLines={1} fontSize="md" color="text.primary">
                {email}
              </Text>
            </VStack>
          );
        },
      },
      {
        Header: jobTitleAndDepartment,
        accessor: (row) => row.user?.microsoft_entra_id?.job_title,
        id: 'jobTitle',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => {
          const userPayload = props.row.original.user?.microsoft_entra_id;
          return (
            <VStack alignItems="start" spacing={0} w="100%">
              <Text noOfLines={1} fontSize="md" color="text.primary" w="100%">
                {userPayload?.job_title || '-'}
              </Text>
              <Text noOfLines={1} fontSize="md" color="text.primary" w="100%">
                {userPayload?.department || '-'}
              </Text>
            </VStack>
          );
        },
      },
      {
        Header: riskDetail,
        accessor: (row: EntraSigninWithUser) =>
          row.signin?.microsoft_entra_id?.sign_in?.risk_level_during_signin,
        id: 'riskDetail',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => {
          const risk = (props.value as string) || 'Low';
          const color =
            risk.toLowerCase() === 'high'
              ? 'red'
              : risk.toLowerCase() === 'medium'
                ? 'orange'
                : 'green';
          return (
            <Tag colorScheme={color} size="md" variant="solid">
              {risk}
            </Tag>
          );
        },
      },
      {
        Header: userPrincipalName,
        accessor: (row) =>
          row.user?.microsoft_entra_id?.user_principal_name ||
          row.signin?.microsoft_entra_id?.user_principal_name,
        id: 'upn',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => (
          <Tooltip label={props.value} placement="top" hasArrow>
            <Text noOfLines={1} fontSize="md" color="text.primary" w="100%">
              {props.value || '-'}
            </Text>
          </Tooltip>
        ),
      },
      {
        Header: appUsed,
        accessor: (row) =>
          row.signin?.microsoft_entra_id?.sign_in?.client_app_used,
        id: 'appUsed',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => (
          <Tooltip label={props.value} placement="top" hasArrow>
            <Text noOfLines={1} fontSize="md" color="text.primary" w="100%">
              {props.value || '-'}
            </Text>
          </Tooltip>
        ),
      },
      {
        Header: location,
        accessor: (row) => {
          const loc = row.signin?.microsoft_entra_id?.sign_in?.location;
          if (!loc) return '-';
          return [loc.city, loc.state, loc.country_or_region]
            .filter(Boolean)
            .join(', ');
        },
        id: 'location',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => (
          <Tooltip label={props.value} placement="top" hasArrow>
            <Text noOfLines={1} fontSize="md" color="text.primary" w="100%">
              {props.value || '-'}
            </Text>
          </Tooltip>
        ),
      },
      {
        Header: timestamp,
        accessor: (row) =>
          row.signin?.microsoft_entra_id?.sign_in?.created_date_time,
        id: 'created',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => {
          const ts = props.value;
          return (
            <Tooltip
              label={ts ? moment(ts).format('MMM D, YYYY h:mma') : '-'}
              placement="top"
              hasArrow
            >
              <Text fontSize="md" color="text.primary" noOfLines={1}>
                {ts ? moment(ts).format('MMM D, YYYY h:mma') : '-'}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        Header: '',
        id: 'actions',
        disableSortBy: true,
        Cell: (props: CellProps<EntraSigninWithUser>) => {
          const userWrapper = props.row.original;

          return (
            <Menu strategy="fixed" isLazy>
              <Tooltip
                label={
                  userRole !== 'admin'
                    ? edrAction.requestPermissions
                    : undefined
                }
              >
                <MenuButton
                  as={Button}
                  size="xs"
                  colorScheme="gray"
                  variant="solid"
                  rightIcon={<KeyboardArrowDownSharpIcon />}
                  isDisabled={!actionsEnabled || userRole !== 'admin'} // Disabled when config doesn't exist or user is not admin
                  _hover={{
                    cursor:
                      actionsEnabled && userRole === 'admin'
                        ? 'pointer'
                        : 'not-allowed',
                  }}
                >
                  {actions}
                </MenuButton>
              </Tooltip>
              <MenuList
                p={0}
                fontSize="xs"
                color="text.primary"
                bg="layer.3"
                zIndex={9999}
              >
                <MenuItem
                  data-testid="universal-logout-item"
                  icon={
                    <UniversalLogoutIcon boxSize={5} color="text.primary" />
                  }
                  onClick={() => openModal('logout', userWrapper)}
                >
                  {universallogoutText}
                </MenuItem>
                <MenuItem
                  data-testid="reset-password-item"
                  icon={<ResetPasswordIcon boxSize={5} color="text.primary" />}
                  onClick={() => openModal('reset', userWrapper)}
                >
                  {resetPasswordText}
                </MenuItem>
                <MenuItem
                  data-testid="enable-account-item"
                  icon={<CheckIcon boxSize={5} color="text.primary" />}
                  onClick={() => openModal('enable', userWrapper)}
                >
                  {enableAccountText}
                </MenuItem>
                <MenuItem
                  data-testid="disable-account-item"
                  icon={<DisableAccountIcon boxSize={5} color="text.primary" />}
                  onClick={() => openModal('disable', userWrapper)}
                >
                  {disableAccountText}
                </MenuItem>
                <MenuItem
                  data-testid="tag-high-risk-item"
                  icon={<TagHighRiskIcon boxSize={5} color="text.primary" />}
                  onClick={() => openModal('risk', userWrapper)}
                >
                  {tagHighRiskText}
                </MenuItem>
              </MenuList>
            </Menu>
          );
        },
      },
    ],
    [actionsEnabled, userRole],
  );

  return (
    <>
      {identityIntegrationEnabled && (
        <VStack w="100%" alignItems="start" spacing={4}>
          {signinHistoryError ? (
            <GraphQLReqStatus loading={false} error={signinHistoryError} />
          ) : signinHistoryLoading && totalItems === 0 ? (
            <VStack w="100%" alignItems="start" spacing={3}>
              <Panel w="100%">
                <Center py={8}>
                  <LoadingSpinner />
                </Center>
              </Panel>
            </VStack>
          ) : historyItems.length === 0 ? (
            <Box>
              <Text color="text.secondary" fontSize="md">
                {noDataFound}
              </Text>
            </Box>
          ) : (
            <VStack w="100%" alignItems="start" spacing={3}>
              <Panel w="100%">
                <FlushPanelContent>
                  <DataTable
                    isLined
                    data={historyItems}
                    columns={dataTableColumns}
                    isPaginable={true}
                    isPaginationManual={true}
                    autoResetPage={false}
                    pageCount={pageCount}
                    pageSize={pageSize}
                    isLoading={signinHistoryLoading}
                    initialState={{
                      pageIndex,
                      pageSize,
                    }}
                    onFetchData={onFetchData}
                    itemCount={totalItems}
                    emptyBodyContent={
                      <Text align={'center'} color="text.secondary">
                        {noDataFound}
                      </Text>
                    }
                  />
                </FlushPanelContent>
              </Panel>
            </VStack>
          )}

          {/* Action Modal */}
          {modalState.isOpen && (
            <Modal
              isOpen={modalState.isOpen}
              onClose={handleCloseModal}
              isCentered
            >
              <ModalOverlay />
              <ModalContent bg="layer.1" borderRadius="base" width="500px">
                <ModalHeader pb={4}>
                  <Text fontWeight={600} fontSize="xl" color="text.primary">
                    {getModalTitle(modalState.action, modalState.user)}
                  </Text>
                </ModalHeader>

                <ModalCloseButton color="text.secondary" />

                <ModalBody
                  borderBottom={'1px solid #525252'}
                  borderTop={'1px solid #525252'}
                >
                  {errorAlert && (
                    <Alert
                      status="error"
                      variant="subtle"
                      style={{
                        alignContent: 'flex-start',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <Box
                        flex="1"
                        style={{ display: 'flex', alignItems: 'flex-start' }}
                      >
                        <AlertIcon color="#B28600" />
                        <Box flex="1" marginLeft={'1.25rem'}>
                          <AlertDescription fontSize="md">
                            {errorAlert}
                          </AlertDescription>
                        </Box>
                      </Box>
                    </Alert>
                  )}

                  <Alert
                    status="warning"
                    variant="subtle"
                    style={{
                      alignContent: 'flex-start',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                    }}
                  >
                    <Box
                      flex="1"
                      style={{ display: 'flex', alignItems: 'flex-start' }}
                    >
                      <InfoOutlineIcon
                        color="#B28600"
                        height={'1.25rem'}
                        width={'1.25rem'}
                        style={{ marginTop: '0.25rem' }}
                      />
                      <Box flex="1" marginLeft={'1.25rem'}>
                        <Text fontSize="md" color="text.primary">
                          {getModalMessage(modalState.action, modalState.user)}
                        </Text>
                      </Box>
                    </Box>
                  </Alert>
                </ModalBody>

                <ModalFooter pt={4} pb={4}>
                  <HStack spacing={3} justifyContent="flex-end" w="100%">
                    <Button
                      variant="solid"
                      size="md"
                      colorScheme="gray"
                      onClick={handleCloseModal}
                      minW="96px"
                      width="61px"
                      height="32px"
                    >
                      {cancel}
                    </Button>

                    <Button
                      variant="solid"
                      size="md"
                      colorScheme="blue"
                      onClick={handleConfirm}
                      minW="140px"
                      width="121px"
                      height="32px"
                      isLoading={actionLoading}
                    >
                      {getConfirmLabel(modalState.action)}
                    </Button>
                  </HStack>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}

          {/* Success banner (auto hides in 5s) */}
          {successAlert && (
            <Box
              position="fixed"
              bottom="16px"
              right="16px"
              zIndex={9999}
              maxW="520px"
            >
              <Alert
                status="success"
                variant="solid"
                borderRadius="base"
                color="text.primary"
              >
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {successAlert}
                </AlertDescription>
              </Alert>
            </Box>
          )}
        </VStack>
      )}
    </>
  );
};
